import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ExternalLink, Globe, BookOpen, GraduationCap, Award,
  AlertCircle, Loader2,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import JsonLd from '../components/JsonLd';
import AdvisorRibbon from '../components/AdvisorRibbon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_LABELS = {
  bachelor: { label: 'Bachelor', color: 'bg-blue-50 text-blue-700 border-blue-700' },
  master: { label: 'Master', color: 'bg-purple-50 text-purple-700 border-purple-700' },
  phd: { label: 'PhD', color: 'bg-teal-50 text-teal-700 border-teal-700' },
  phd_studentship: { label: 'PhD Studentship', color: 'bg-cyan-50 text-cyan-700 border-cyan-700' },
};

const TYPE_LABELS = {
  fully_funded: { label: 'Fully Funded', color: 'bg-green-50 text-green-700 border-green-700' },
  university: { label: 'University', color: 'bg-amber-50 text-amber-700 border-amber-700' },
};

export default function ScholarshipDetail() {
  const { idOrSlug } = useParams();
  const navigate = useNavigate();
  const [scholarship, setScholarship] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/scholarships/${encodeURIComponent(idOrSlug)}`)
      .then((res) => {
        if (cancelled) return;
        setScholarship(res.data?.scholarship || null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setError('Scholarship not found');
        } else {
          setError('Failed to load scholarship');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idOrSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-amber-700">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p className="text-sm">Loading scholarship…</p>
        </div>
      </div>
    );
  }

  if (error || !scholarship) {
    return (
      <div className="min-h-screen bg-white text-gray-900">
        <SEOHead title="Scholarship Not Found" description="The scholarship you're looking for is no longer available." path={`/scholarships/${idOrSlug}`} noIndex />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">{error || 'Scholarship not found'}</h1>
          <p className="text-gray-600 mb-6">
            It may have been removed, or the link could be incorrect.
          </p>
          <button
            onClick={() => navigate('/scholarships')}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-semibold hover:from-amber-500 hover:to-orange-500 transition-all"
            data-testid="back-to-scholarships-btn"
          >
            Browse All Scholarships
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const levels = scholarship.level || [];
  const types = scholarship.type || [];
  const metaDesc = (scholarship.description || `${scholarship.title} at ${scholarship.university || ''}`).slice(0, 160);
  const canonicalSlug = scholarship.slug || idOrSlug;

  // ───────── JSON-LD: Scholarship as EducationalOccupationalProgram ─────────
  // (schema.org doesn't have a "Scholarship" type Google supports, so we model
  // it as an EducationalOccupationalProgram with a financialAidEligible flag.)
  const levelMap = {
    bachelor: "Bachelor's degree",
    master: "Master's degree",
    phd: 'Doctoral degree',
    phd_studentship: 'Doctoral degree',
  };
  const educationalLevel = levels.map((l) => levelMap[l]).filter(Boolean);
  const scholarshipSchema = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOccupationalProgram',
    name: scholarship.title,
    description: scholarship.description || scholarship.title,
    ...(scholarship.university
      ? {
          provider: {
            '@type': 'CollegeOrUniversity',
            name: scholarship.university,
            ...(scholarship.country
              ? { address: { '@type': 'PostalAddress', addressCountry: scholarship.country } }
              : {}),
          },
        }
      : {}),
    ...(educationalLevel.length ? { educationalCredentialAwarded: educationalLevel } : {}),
    ...(scholarship.eligibility ? { educationalProgramMode: 'full-time', programPrerequisites: scholarship.eligibility } : {}),
    ...(scholarship.deadline ? { applicationDeadline: scholarship.deadline } : {}),
    ...(scholarship.official_link ? { url: scholarship.official_link } : {}),
    financialAidEligible: scholarship.funding_amount || 'Fully Funded',
    occupationalCategory: 'Higher Education Scholarship',
  };
  // Also emit Article schema so the page can appear in general Google index
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${scholarship.title}${scholarship.university ? ' — ' + scholarship.university : ''}`,
    description: metaDesc,
    author: { '@type': 'Organization', name: 'Nerd Nation' },
    publisher: {
      '@type': 'Organization',
      name: 'Nerd Nation',
      logo: { '@type': 'ImageObject', url: 'https://nerdnation.world/logo512.png' },
    },
    ...(scholarship.created_at ? { datePublished: scholarship.created_at } : {}),
    ...(scholarship.updated_at ? { dateModified: scholarship.updated_at } : {}),
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://nerdnation.world/scholarships/${canonicalSlug}` },
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <SEOHead
        title={`${scholarship.title}${scholarship.university ? ' — ' + scholarship.university : ''}`}
        description={metaDesc}
        path={`/scholarships/${canonicalSlug}`}
        ogType="scholarship"
      />
      <JsonLd data={[scholarshipSchema, articleSchema]} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <Link
          to="/scholarships"
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-amber-700 mb-5 transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" /> All Scholarships
        </Link>

        <article className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <header className="px-5 sm:px-8 py-6 border-b border-gray-200">
            <div className="flex flex-wrap gap-1.5 mb-3">
              {levels.map((lv) => {
                const cfg = LEVEL_LABELS[lv];
                return cfg ? (
                  <span key={lv} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                    {cfg.label}
                  </span>
                ) : null;
              })}
              {types.map((tp) => {
                const cfg = TYPE_LABELS[tp];
                return cfg ? (
                  <span key={tp} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                    {cfg.label}
                  </span>
                ) : null;
              })}
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold leading-snug mb-2">
              {scholarship.title}
            </h1>
            {scholarship.university && (
              <p className="text-amber-700 text-base font-medium">{scholarship.university}</p>
            )}
            {scholarship.country && (
              <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                <Globe className="w-3.5 h-3.5" />
                {scholarship.country}
              </p>
            )}
          </header>

          {/* Quick info */}
          {(scholarship.funding_amount || scholarship.deadline) && (
            <div className="px-5 sm:px-8 py-5 border-b border-gray-200">
              <div className="flex flex-wrap gap-3">
                {scholarship.funding_amount && (
                  <div className="bg-green-50 border border-green-800 rounded-lg px-4 py-3 flex-1 min-w-[180px]">
                    <p className="text-green-700 text-[10px] font-semibold uppercase tracking-wide mb-0.5">Funding</p>
                    <p className="text-gray-900 text-sm">{scholarship.funding_amount}</p>
                  </div>
                )}
                {scholarship.deadline && (
                  <div className="bg-amber-50 border border-amber-800 rounded-lg px-4 py-3 flex-1 min-w-[180px]">
                    <p className="text-amber-700 text-[10px] font-semibold uppercase tracking-wide mb-0.5">Deadline</p>
                    <p className="text-gray-900 text-sm">{scholarship.deadline}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Body sections */}
          <div className="px-5 sm:px-8 py-6 space-y-6">
            {scholarship.description && (
              <section>
                <h2 className="text-gray-700 text-xs font-semibold uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5 text-amber-500" /> About
                </h2>
                <p className="text-gray-700 text-sm leading-relaxed">{scholarship.description}</p>
              </section>
            )}

            {scholarship.eligibility && (
              <section>
                <h2 className="text-gray-700 text-xs font-semibold uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <Award className="w-3.5 h-3.5 text-amber-500" /> Eligibility
                </h2>
                <p className="text-gray-700 text-sm leading-relaxed">{scholarship.eligibility}</p>
              </section>
            )}

            {scholarship.application_process && (
              <section>
                <h2 className="text-gray-700 text-xs font-semibold uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <GraduationCap className="w-3.5 h-3.5 text-amber-500" /> How to Apply
                </h2>
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">{scholarship.application_process}</p>
              </section>
            )}

            {scholarship.official_link && (
              <a
                href={scholarship.official_link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="apply-now-btn"
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-semibold text-sm hover:from-amber-500 hover:to-orange-500 transition-all duration-150 active:scale-95"
              >
                Apply on Official Website <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </article>
      </div>
      <AdvisorRibbon />
      <Footer />
    </div>
  );
}
